import React, { useState, useMemo, useRef } from "react";

const TYPE_ORDER = [
  "genotype",
  "mechanism",
  "tissue",
  "phenotype",
  "diagnosis",
];
const TYPE_LABEL = {
  genotype: "Genotype",
  mechanism: "Mechanism",
  tissue: "Tissue",
  phenotype: "Phenotype",
  diagnosis: "Diagnosis",
};

const TIER = {
  established: {
    label: "Established",
    note: "Supported by retrieved literature",
    stroke: "hsl(var(--primary))",
    dash: "none",
    weight: 2.4,
  },
  inferred: {
    label: "Inferred",
    note: "Derived from case findings",
    stroke: "#d4a24a",
    dash: "none",
    weight: 1.8,
  },
  hypothesis: {
    label: "Hypothesis",
    note: "Model's own proposition, testable",
    stroke: "#e0794a",
    dash: "6 5",
    weight: 1.8,
  },
};
const tierOf = (t) => TIER[t] || TIER.inferred;

export default function PathwayGraph({ graph }) {
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [expandedNode, setExpandedNode] = useState(null);
  const [hoverEdge, setHoverEdge] = useState(null);
  const wrapRef = useRef(null);

  const {
    nodes = [],
    edges = [],
    anchor_registry = {},
    ungrounded = [],
  } = graph || {};

  const layout = useMemo(() => {
    const cols = TYPE_ORDER.map((t) => ({
      type: t,
      nodes: nodes.filter((n) => n.type === t),
    })).filter((c) => c.nodes.length > 0);
    const colW = 200;
    const rowH = 108;
    const nodeW = 168;
    const nodeH = 72;
    const pos = {};
    cols.forEach((col, ci) =>
      col.nodes.forEach((n, ri) => {
        pos[n.id] = {
          x: 20 + ci * colW + (colW - nodeW) / 2,
          y: 60 + ri * rowH,
          w: nodeW,
          h: nodeH,
        };
      }),
    );
    const maxRows = Math.max(1, ...cols.map((c) => c.nodes.length));
    const totalWidth = 40 + cols.length * colW;
    return {
      cols,
      colW,
      pos,
      height: 60 + maxRows * rowH + 20,
      nodeW,
      nodeH,
      totalWidth,
    };
  }, [nodes]);

  const edgePath = (e) => {
    const s = layout.pos[e.source],
      t = layout.pos[e.target];
    if (!s || !t) return null;
    const x1 = s.x + s.w,
      y1 = s.y + s.h / 2,
      x2 = t.x,
      y2 = t.y + t.h / 2,
      mx = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
  };

  if (!graph || nodes.length === 0)
    return <div style={S.empty}>No grounded pathway.</div>;

  return (
    <div style={S.root} ref={wrapRef}>
      <div style={S.header}>
        <div>
          <div style={S.eyebrow}>Genotype to phenotype</div>
          <h3 style={S.title}>Reasoning pathway</h3>
          <p style={S.sub}>
            Every link traces to evidence the system actually used. Line style
            marks how well-supported each step is. Select any link to see the
            reasoning and its source.
          </p>
        </div>
        <Legend />
      </div>

      <div style={S.canvasWrap}>
        <svg
          width={layout.totalWidth}
          height={layout.height}
          viewBox={`0 0 ${layout.totalWidth} ${layout.height}`}
          style={{ display: "block", minWidth: layout.totalWidth }}
        >
          {layout.cols.map((col, ci) => {
            const x = 20 + ci * layout.colW + layout.colW / 2;
            return (
              <text
                key={col.type}
                x={x}
                y={30}
                textAnchor="middle"
                style={S.colHeader}
              >
                {TYPE_LABEL[col.type] || col.type}
              </text>
            );
          })}
          {edges.map((e, i) => {
            const d = edgePath(e);
            if (!d) return null;
            const tier = tierOf(e.tier);
            const isSel =
              selectedEdge &&
              selectedEdge.source === e.source &&
              selectedEdge.target === e.target;
            const isHover =
              hoverEdge === i ||
              expandedNode === e.source ||
              expandedNode === e.target;
            return (
              <g key={i}>
                <path
                  d={d}
                  stroke="transparent"
                  strokeWidth={16}
                  fill="none"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedEdge(e)}
                  onMouseEnter={() => setHoverEdge(i)}
                  onMouseLeave={() => setHoverEdge(null)}
                />
                <path
                  d={d}
                  stroke={tier.stroke}
                  strokeWidth={
                    isSel || isHover ? tier.weight + 1.2 : tier.weight
                  }
                  strokeDasharray={tier.dash === "none" ? undefined : tier.dash}
                  fill="none"
                  opacity={isSel || isHover || !selectedEdge ? 1 : 0.35}
                  style={{ transition: "opacity .15s, stroke-width .15s" }}
                  markerEnd={`url(#arrow-${e.tier || "inferred"})`}
                />
              </g>
            );
          })}
          <defs>
            {Object.entries(TIER).map(([k, t]) => (
              <marker
                key={k}
                id={`arrow-${k}`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={t.stroke} />
              </marker>
            ))}
          </defs>
          {nodes.map((n) => {
            const p = layout.pos[n.id];
            if (!p) return null;
            const expanded = expandedNode === n.id;
            return (
              <g
                key={n.id}
                transform={`translate(${p.x}, ${p.y})`}
                style={{ cursor: "pointer" }}
                onClick={() => setExpandedNode(expanded ? null : n.id)}
              >
                <rect
                  width={p.w}
                  height={p.h}
                  rx={10}
                  fill="hsl(var(--secondary))"
                  stroke={
                    expanded ? "hsl(var(--primary))" : "hsl(var(--border))"
                  }
                  strokeWidth={expanded ? 2 : 1}
                  style={{ transition: "stroke .15s" }}
                />
                <foreignObject x={10} y={0} width={p.w - 20} height={p.h}>
                  <div style={S.nodeInner}>
                    <div style={S.nodeType}>{TYPE_LABEL[n.type] || n.type}</div>
                    <div style={S.nodeLabel}>{n.label}</div>
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>

      {expandedNode && (
        <NodeDetail
          node={nodes.find((n) => n.id === expandedNode)}
          registry={anchor_registry}
          onClose={() => setExpandedNode(null)}
        />
      )}
      {selectedEdge && (
        <EdgePanel
          edge={selectedEdge}
          nodes={nodes}
          registry={anchor_registry}
          onClose={() => setSelectedEdge(null)}
        />
      )}
      {ungrounded && ungrounded.length > 0 && (
        <div style={S.ungrounded}>
          <span style={S.ungroundedDot} />
          {ungrounded.length} proposed link
          {ungrounded.length > 1 ? "s were" : " was"} left out because{" "}
          {ungrounded.length > 1 ? "they" : "it"} could not be traced to any
          finding, hypothesis, or paper in this case.
        </div>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div style={S.legend}>
      {Object.entries(TIER).map(([k, t]) => (
        <div key={k} style={S.legendRow}>
          <svg width="34" height="12" style={{ flex: "none" }}>
            <line
              x1="1"
              y1="6"
              x2="33"
              y2="6"
              stroke={t.stroke}
              strokeWidth={t.weight}
              strokeDasharray={t.dash === "none" ? undefined : t.dash}
            />
          </svg>
          <div>
            <div style={S.legendLabel}>{t.label}</div>
            <div style={S.legendNote}>{t.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NodeDetail({ node, registry, onClose }) {
  if (!node) return null;
  const a = registry[node.anchor] || "unresolved anchor";
  return (
    <div style={S.detail}>
      <div style={S.detailHead}>
        <div>
          <span style={S.detailKind}>{TYPE_LABEL[node.type] || node.type}</span>
          <span style={S.detailTitle}>{node.label}</span>
        </div>
        <button style={S.close} onClick={onClose}>
          ×
        </button>
      </div>
      <div style={S.detailRow}>
        <span style={S.detailK}>Anchored to</span>
        <span style={S.detailV}>{a}</span>
      </div>
    </div>
  );
}

function EdgePanel({ edge, nodes, registry, onClose }) {
  const s = nodes.find((n) => n.id === edge.source),
    t = nodes.find((n) => n.id === edge.target);
  const tier = tierOf(edge.tier);
  const a = registry[edge.anchor] || "unresolved anchor";
  const isPaper = (edge.anchor || "").startsWith("paper:");
  return (
    <div style={S.detail}>
      <div style={S.detailHead}>
        <div style={S.edgeFlow}>
          <span style={S.flowNode}>{s ? s.label : edge.source}</span>
          <span style={S.flowArrow}>→</span>
          <span style={S.flowNode}>{t ? t.label : edge.target}</span>
        </div>
        <button style={S.close} onClick={onClose}>
          ×
        </button>
      </div>
      <div
        style={{ ...S.tierPill, borderColor: tier.stroke, color: tier.stroke }}
      >
        {tier.label}
        <span style={S.tierPillNote}>· {tier.note}</span>
      </div>
      <div style={S.evidence}>
        {edge.evidence || "No reasoning text provided."}
      </div>
      <div style={S.detailRow}>
        <span style={S.detailK}>{isPaper ? "Source" : "Anchored to"}</span>
        <span style={S.detailV}>{a}</span>
      </div>
    </div>
  );
}

const S = {
  root: {
    fontFamily: "inherit",
    background: "hsl(var(--card) / 0.4)",
    border: "1px solid hsl(var(--border))",
    borderRadius: 16,
    padding: "24px 24px 20px",
    color: "hsl(var(--foreground))",
    maxWidth: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 24,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "hsl(var(--muted-foreground))",
    fontWeight: 600,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    margin: "4px 0 6px",
    letterSpacing: "-0.01em",
    color: "hsl(var(--foreground))",
  },
  sub: {
    fontSize: 13.5,
    lineHeight: 1.5,
    color: "hsl(var(--muted-foreground))",
    maxWidth: 440,
    margin: 0,
  },
  legend: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "hsl(var(--secondary))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 12,
    padding: "12px 14px",
  },
  legendRow: { display: "flex", alignItems: "center", gap: 10 },
  legendLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    lineHeight: 1.1,
    color: "hsl(var(--foreground))",
  },
  legendNote: { fontSize: 11, color: "hsl(var(--muted-foreground))" },
  canvasWrap: {
    background: "hsl(var(--secondary) / 0.5)",
    border: "1px solid hsl(var(--border))",
    borderRadius: 12,
    padding: "8px 4px",
    overflowX: "auto",
    maxWidth: "100%",
  },
  colHeader: {
    fontSize: 10.5,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fill: "hsl(var(--muted-foreground))",
    fontWeight: 600,
  },
  nodeInner: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  nodeType: {
    fontSize: 9.5,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "hsl(var(--muted-foreground))",
    fontWeight: 600,
    marginBottom: 2,
  },
  nodeLabel: {
    fontSize: 12,
    lineHeight: 1.25,
    fontWeight: 550,
    color: "hsl(var(--foreground))",
  },
  detail: {
    marginTop: 14,
    background: "hsl(var(--secondary))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 12,
    padding: "14px 16px",
  },
  detailHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 10,
  },
  detailKind: {
    fontSize: 10,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "hsl(var(--muted-foreground))",
    fontWeight: 600,
    marginRight: 8,
  },
  detailTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "hsl(var(--foreground))",
  },
  edgeFlow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  flowNode: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "hsl(var(--foreground))",
  },
  flowArrow: { color: "hsl(var(--muted-foreground))" },
  close: {
    border: "none",
    background: "none",
    fontSize: 22,
    lineHeight: 1,
    color: "hsl(var(--muted-foreground))",
    cursor: "pointer",
    padding: 0,
  },
  tierPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11.5,
    fontWeight: 600,
    border: "1px solid",
    borderRadius: 999,
    padding: "3px 10px",
    marginBottom: 10,
  },
  tierPillNote: { fontWeight: 400, opacity: 0.8 },
  evidence: {
    fontSize: 13.5,
    lineHeight: 1.55,
    color: "hsl(var(--foreground))",
    marginBottom: 10,
  },
  detailRow: {
    display: "flex",
    gap: 10,
    fontSize: 12.5,
    alignItems: "baseline",
  },
  detailK: {
    color: "hsl(var(--muted-foreground))",
    fontWeight: 600,
    minWidth: 78,
    textTransform: "uppercase",
    fontSize: 10.5,
    letterSpacing: "0.06em",
  },
  detailV: { color: "hsl(var(--foreground))", lineHeight: 1.4 },
  ungrounded: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "hsl(var(--muted-foreground))",
    background: "hsl(var(--secondary))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 10,
    padding: "9px 12px",
  },
  ungroundedDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: "#d4a24a",
    flex: "none",
  },
  empty: {
    padding: 24,
    textAlign: "center",
    color: "hsl(var(--muted-foreground))",
  },
};
